import clsx from 'clsx'
import Image from 'next/image'

import Link from '@/atoms/Link/Link'

/* assets */
import { ReactComponent as ArrowRight } from 'public/assets/icons/chevron-right.svg'

interface Props {
  title: string
  description?: string
  image?: StrapiImageFile
  cta?: StrapiLinkSharedComponent
  lists: {
    id: string | number
    title: string
    description: string
    image?: {
      url: string
      alternativeText: string
      width: number
      height: number
    }
  }[]
}

function RevenueEngine({ title, description, image, cta, lists }: Props) {
  return (
    <section className="font-body text-white bg-blue-web-90">
      <div className="container | py-16 md:py-20">
        <div
          className={clsx(
            image?.url ? 'text-left' : 'text-left md:text-center'
          )}
        >
          {image?.url ? (
            <div className="flex items-center justify-between">
              <h3
                className="font-heading text-3xl md:text-5xl font-semibold | max-w-col-8 mb-5 md:mb-8"
                dangerouslySetInnerHTML={{ __html: title }}
              />

              <Image
                src={image.url}
                alt={image.alternativeText ?? ''}
                width={image.width}
                height={image.height}
                className="hidden md:block"
              />
            </div>
          ) : (
            <h3 className="font-heading text-3xl md:text-5xl font-semibold | max-w-col-10 mx-auto mb-14 md:mb-20">
              {title}
            </h3>
          )}
          {image?.url ? (
            <div>
              {description ? (
                <div className="text-lg md:text-xl | max-w-col-8 mb-5 md:mb-8">
                  {description}
                </div>
              ) : null}
              {cta ? (
                <Link
                  href={cta.href}
                  target={cta.target}
                  className="flex items-center gap-2 font-medium | underline underline-offset-8 mb-12 md:mb-18"
                >
                  {cta.title}
                  <ArrowRight />
                </Link>
              ) : null}
            </div>
          ) : (
            <div>
              {description ? (
                <div className="text-lg md:text-xl | max-w-col-8 mx-auto mb-11 md:mb-8">
                  {description}
                </div>
              ) : null}
              {cta ? (
                <div className="flex flex-col md:flex-row justify-center">
                  <Link
                    href={cta.href}
                    className="new-btn-primary | font-medium | mb-12 md:mb-18"
                  >
                    {cta.title}
                  </Link>
                </div>
              ) : null}
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {lists.map((list, index) => (
            <div
              key={list.id}
              className={clsx(
                'flex  text-blue-web-90 bg-white | rounded-lg py-9 px-5',
                index === 0 &&
                  'flex-col lg:flex-row gap-7 lg:gap-28 lg:col-span-12 | md:px-16',
                index === 1 &&
                  'flex-col lg:flex-row gap-8 lg:col-span-7 | md:p-12',
                index === 2 &&
                  'flex flex-col gap-7 lg:col-span-5 | lg:px-10 md:py-12'
              )}
            >
              <div
                className={clsx(
                  index === 0 && 'flex flex-col justify-center',
                  index === 2 && 'flex-grow'
                )}
              >
                <h3
                  className="font-heading text-2xl md:text-3xl font-semibold | mb-3 md:mb-6"
                  dangerouslySetInnerHTML={{ __html: list.title }}
                />
                <span
                  className="text-base md:text-lg text-gray-60"
                  dangerouslySetInnerHTML={{ __html: list.description }}
                />
              </div>
              <Image
                src={list.image?.url ?? ''}
                alt={list.image?.alternativeText ?? ''}
                width={list.image?.width}
                height={list.image?.height}
                className={clsx(index === 0 && 'lg:max-w-lg')}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default RevenueEngine
